import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Heart, Search, Download, Edit2, Trash2, Save, X, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function AdminDonations() {
  const [donations, setDonations] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCampaign, setFilterCampaign] = useState("all");
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dRes, cRes] = await Promise.all([
        axios.get(`${API}/admin/donations`, { withCredentials: true }),
        axios.get(`${API}/campaigns?active_only=false`, { withCredentials: true }),
      ]);
      setDonations(dRes.data.donations || dRes.data);
      setCampaigns(cRes.data || []);
    } catch (err) {
      toast.error("Failed to load donations");
    } finally { setLoading(false); }
  };

  const openEdit = (d) => {
    setEditing({
      ...d,
      donor_name: d.donor_name || "",
      donor_email: d.donor_email || "",
      amount: d.amount || 0,
      campaign_id: d.campaign_id || "",
      payment_method: d.payment_method || "manual",
      status: d.status || "completed",
      notes: d.notes || "",
      anonymous: !!d.anonymous,
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editing) return;
    if (!editing.donor_name && !editing.anonymous) { toast.error("Donor name is required (or check Anonymous)"); return; }
    const amt = parseFloat(editing.amount);
    if (isNaN(amt) || amt < 0) { toast.error("Amount must be a valid number"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/donations/${editing.donation_id}`, {
        donor_name: editing.donor_name,
        donor_email: editing.donor_email,
        amount: amt,
        campaign_id: editing.campaign_id || null,
        payment_method: editing.payment_method,
        status: editing.status,
        notes: editing.notes,
        anonymous: editing.anonymous,
      }, { withCredentials: true });
      toast.success("Donation updated");
      setEditOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await axios.delete(`${API}/admin/donations/${toDelete.donation_id}`, { withCredentials: true });
      toast.success("Donation deleted");
      setDeleteOpen(false);
      setToDelete(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = donations.filter(d => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      (d.donor_name || "").toLowerCase().includes(q) ||
      (d.donor_email || "").toLowerCase().includes(q) ||
      (d.notes || "").toLowerCase().includes(q);
    const matchesCampaign = filterCampaign === "all" ||
      (filterCampaign === "_unassigned" ? !d.campaign_id : d.campaign_id === filterCampaign);
    return matchesSearch && matchesCampaign;
  });

  const totalAmount = filtered.reduce((sum, d) => sum + (d.amount || 0), 0);
  const recurringDonations = filtered.filter(d => d.recurring).length;

  const exportCSV = () => {
    const headers = ["Date", "Donor", "Email", "Amount", "Recurring", "Campaign", "Method", "Status", "Notes"];
    const rows = filtered.map(d => [
      d.created_at ? format(new Date(d.created_at), "yyyy-MM-dd") : "",
      d.anonymous ? "Anonymous" : (d.donor_name || ""),
      d.donor_email || "",
      d.amount || 0,
      d.recurring ? "Yes" : "No",
      d.campaign || "General",
      d.payment_method || "",
      d.status || "",
      (d.notes || "").replace(/"/g, '""'),
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `donations-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
  };

  return (
    <AdminLayout currentPage="Donations">
      <div className="p-8" data-testid="admin-donations">
        <div className="flex flex-wrap justify-between items-center mb-8 gap-4">
          <div>
            <h1 className="text-heading-2 text-deep-blue">Donations</h1>
            <p className="text-slate-600">View, edit, and manage every donation across all campaigns</p>
          </div>
          <Button variant="outline" onClick={exportCSV} data-testid="export-donations-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <p className="text-sm text-slate-500">Total Raised (filtered)</p>
            <p className="text-3xl font-bold text-deep-blue">{fmtMoney(totalAmount)}</p>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <p className="text-sm text-slate-500">Total Donations (filtered)</p>
            <p className="text-3xl font-bold text-deep-blue">{filtered.length}</p>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <p className="text-sm text-slate-500">Recurring Donors</p>
            <p className="text-3xl font-bold text-deep-blue">{recurringDonations}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search donors, emails, notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-donations"
            />
          </div>
          <select
            value={filterCampaign}
            onChange={(e) => setFilterCampaign(e.target.value)}
            className="border border-input rounded-md h-10 px-3 text-sm bg-white"
            data-testid="filter-campaign"
          >
            <option value="all">All Campaigns</option>
            <option value="_unassigned">Unassigned / General</option>
            {campaigns.map(c => <option key={c.campaign_id} value={c.campaign_id}>{c.title}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="bg-white rounded-lg p-6 h-20 animate-pulse" />)}</div>
        ) : filtered.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-3 font-semibold text-slate-700 text-sm">Date</th>
                  <th className="text-left p-3 font-semibold text-slate-700 text-sm">Donor</th>
                  <th className="text-right p-3 font-semibold text-slate-700 text-sm">Amount</th>
                  <th className="text-left p-3 font-semibold text-slate-700 text-sm">Campaign</th>
                  <th className="text-left p-3 font-semibold text-slate-700 text-sm">Method</th>
                  <th className="text-left p-3 font-semibold text-slate-700 text-sm">Status</th>
                  <th className="text-right p-3 font-semibold text-slate-700 text-sm">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(donation => (
                  <tr key={donation.donation_id} data-testid={`donation-row-${donation.donation_id}`} className="hover:bg-slate-50">
                    <td className="p-3 text-slate-600 text-sm whitespace-nowrap">
                      {donation.created_at ? format(new Date(donation.created_at), "MMM d, yyyy") : ""}
                    </td>
                    <td className="p-3">
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{donation.anonymous ? "Anonymous" : (donation.donor_name || "—")}</p>
                        {donation.donor_email && <p className="text-xs text-slate-500">{donation.donor_email}</p>}
                        {donation.is_manual && <span className="inline-block mt-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">MANUAL</span>}
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <span className="text-base font-semibold text-green-600">{fmtMoney(donation.amount)}</span>
                    </td>
                    <td className="p-3 text-slate-600 text-sm">{donation.campaign || "General"}</td>
                    <td className="p-3 text-slate-600 text-xs capitalize">{donation.payment_method || (donation.is_manual ? "manual" : "stripe")}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        donation.status === "completed" || donation.payment_status === "paid"
                          ? "bg-green-100 text-green-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}>
                        {donation.status || donation.payment_status || "—"}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(donation)} data-testid={`edit-${donation.donation_id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => { setToDelete(donation); setDeleteOpen(true); }} data-testid={`delete-${donation.donation_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg p-12 text-center">
            <Heart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No donations match your filters</h3>
            <p className="text-slate-500">Adjust your search or campaign filter.</p>
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="edit-donation-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-deep-blue" /> Edit Donation</DialogTitle>
            </DialogHeader>
            {editing && (
              <div className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="ed-name">Donor Name</Label>
                    <Input id="ed-name" value={editing.donor_name} onChange={(e) => setEditing({...editing, donor_name: e.target.value})} data-testid="edit-donor-name" />
                  </div>
                  <div>
                    <Label htmlFor="ed-email">Donor Email</Label>
                    <Input id="ed-email" type="email" value={editing.donor_email} onChange={(e) => setEditing({...editing, donor_email: e.target.value})} data-testid="edit-donor-email" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="ed-amount">Amount (USD)</Label>
                    <Input id="ed-amount" type="number" step="0.01" min="0" value={editing.amount} onChange={(e) => setEditing({...editing, amount: e.target.value})} data-testid="edit-amount" />
                  </div>
                  <div>
                    <Label htmlFor="ed-method">Payment Method</Label>
                    <select id="ed-method" value={editing.payment_method} onChange={(e) => setEditing({...editing, payment_method: e.target.value})} className="w-full border border-input rounded-md h-10 px-3 text-sm bg-background" data-testid="edit-method">
                      <option value="manual">Other / Manual</option>
                      <option value="cash">Cash</option>
                      <option value="check">Check</option>
                      <option value="wire">Bank Wire</option>
                      <option value="venmo">Venmo / Cash App</option>
                      <option value="paypal">PayPal</option>
                      <option value="stripe">Stripe (online)</option>
                    </select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="ed-campaign">Campaign</Label>
                    <select id="ed-campaign" value={editing.campaign_id} onChange={(e) => setEditing({...editing, campaign_id: e.target.value})} className="w-full border border-input rounded-md h-10 px-3 text-sm bg-background" data-testid="edit-campaign">
                      <option value="">— General / Unassigned —</option>
                      {campaigns.map(c => <option key={c.campaign_id} value={c.campaign_id}>{c.title}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="ed-status">Status</Label>
                    <select id="ed-status" value={editing.status} onChange={(e) => setEditing({...editing, status: e.target.value})} className="w-full border border-input rounded-md h-10 px-3 text-sm bg-background" data-testid="edit-status">
                      <option value="completed">Completed</option>
                      <option value="pending">Pending</option>
                      <option value="refunded">Refunded</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="ed-notes">Notes</Label>
                  <Input id="ed-notes" value={editing.notes} onChange={(e) => setEditing({...editing, notes: e.target.value})} placeholder="Check #1234, received Mar 5..." data-testid="edit-notes" />
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={editing.anonymous} onChange={(e) => setEditing({...editing, anonymous: e.target.checked})} />
                  Anonymous donation (hide name publicly)
                </label>
                {editing.edited_by_name && (
                  <p className="text-xs text-slate-400">Last edited by {editing.edited_by_name} on {editing.edited_at ? format(new Date(editing.edited_at), "MMM d, yyyy 'at' h:mm a") : ""}</p>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setEditOpen(false)}><X className="w-4 h-4 mr-2" /> Cancel</Button>
                  <Button onClick={saveEdit} disabled={saving} className="bg-deep-blue text-white" data-testid="save-edit-donation">
                    <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <DialogContent className="sm:max-w-md" data-testid="delete-donation-dialog">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2"><Trash2 className="w-5 h-5" /> Delete Donation?</DialogTitle>
            </DialogHeader>
            {toDelete && (
              <div className="space-y-3">
                <p className="text-sm text-slate-700">
                  This will permanently delete the <strong>{fmtMoney(toDelete.amount)}</strong> donation from{" "}
                  <strong>{toDelete.anonymous ? "Anonymous" : toDelete.donor_name}</strong>.
                  {toDelete.payment_method === "stripe" && (
                    <span className="block mt-2 text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                      ⚠ This was a Stripe online donation. Deleting it here will NOT refund the donor — issue a refund in Stripe separately.
                    </span>
                  )}
                </p>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
                  <Button variant="destructive" onClick={confirmDelete} data-testid="confirm-delete-donation">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Permanently
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
